import React from 'react';
import classNames from 'classnames';
import get from 'lodash.get';

import ArticleContentSlider from 'components/ArticleContentSlider';
import Ad from 'components/Ad';
import BlockQuote from 'components/BlockQuote';
import { EmbedWidget } from 'components/EmbedWidget';
import FeatureLink from 'components/FeatureLink';
import ImageLiftout from 'components/ImageLiftout';
import InlineImage from 'components/InlineImage';
import InlineVideo from 'components/InlineVideo';
import InlineNewsletterSignup from 'components/NewsletterSignup/Inline';
import LiftOut from 'components/LiftOut';
import Link from 'components/Link';
import NewsvinePoll from 'components/NewsvinePoll';
import Product from 'components/ListicleProduct';
import PullQuote from 'components/PullQuote';
import RecipeCard from 'components/TodayCards/Recipe';
import SlideshowCard from 'components/TodayCards/Slideshow';

import {
  getDebugComponent,
  getRecommendationComponent,
} from './getComponent';

function htmlElement(item, i, path) {
  const {
    element, html = [], insertEndmark, ecommerceEnabled,
  } = item;
  // `Type` must be capitalized for JSX to recognize it as a dynamic tag name.
  const Type = element ?? 'P';

  const className = classNames({
    endmark: insertEndmark,
    'break-above': Type === 'ul',
    ecom: Boolean(ecommerceEnabled),
  });

  if (!html || !html.length) {
    return <Type key={i} className={className} />;
  }

  if (Type === 'blockquote') {
    const text = html.replace(/(<([^>]+)>)/ig, '');

    if (/class=['"]pullquote["']/.test(html)) {
      return <PullQuote key={i} text={text} />;
    }

    return <BlockQuote key={i} text={text} />;
  }

  if (
    ['h2', 'h3', 'h4'].includes(Type.toLowerCase())
    && !html.includes('<a')
  ) {
    const cleanTitle = html.replace(/[^A-Za-z0-9]/g, '');
    const anchorId = `anchor-${cleanTitle}`;
    return (
      <Link key={i} href={`${path}#${anchorId}`} id={anchorId} className="scrollLink">
        <Type className={className} dangerouslySetInnerHTML={{ __html: html }} />
      </Link>
    );
  }

  return <Type key={i} className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

function getImageComponent(item, i) {
  const { ecommerceEnabled, presentation } = item;
  if (presentation.size === 'fullwidth' && !ecommerceEnabled) {
    return <ImageLiftout content={item} key={i} />;
  }
  return (
    <InlineImage
      content={item}
      presentation={presentation.size}
      key={i}
      ecommerceEnabled={ecommerceEnabled}
    />
  );
}

function getWidgetComponent(item, i) {
  if (!item.widget) {
    return null;
  }

  const {
    presentation: {
      size,
    },
    widget,
    widget: {
      name,
      properties = {},
    },
  } = item;

  switch (name) {
    case 'nbc_blockquote': {
      const {
        'nbcblockquote-text': text,
        'nbcblockquote-attributionlink': source,
        'nbcblockquote-attribution': attribution,
      } = properties;
      return (
        <BlockQuote attribution={attribution} key={i} source={source} text={text} />
      );
    }

    case 'nbc_liftout': {
      const {
        'liftout-text': text,
      } = properties;
      return (
        <LiftOut key={i} size={size} text={text} />
      );
    }

    case 'nbc_pullquote': // intentional fall-through
    case 'nbc_pull_quote': {
      const {
        'pullquote-attribution': attribution,
        'pullquote-text': text,
        quip,
      } = properties;
      return (
        <PullQuote attribution={attribution} key={i} quip={quip} text={text} size={size} />
      );
    }

    case 'nbc_featuredlink':
      return <FeatureLink key={i} widget={widget} />;

    case 'newsvine_large_poll':
      return <NewsvinePoll key={i} widget={widget} />;

    case 'CUSTOM_EMBED': {
      switch (properties.embed.type) {
        case 'BLOCKQUOTE': {
          const {
            attribution,
            attributionUrl: source,
            text,
          } = properties.embed;
          return (
            <BlockQuote attribution={attribution} key={i} source={source} text={text} />
          );
        }

        case 'LIFT_OUT': {
          const { text } = properties.embed;
          return (
            <LiftOut key={i} size={size} text={text} />
          );
        }

        case 'PULL_QUOTE': {
          const { attribution, quip, text } = properties.embed;
          return (
            <PullQuote attribution={attribution} key={i} quip={quip} text={text} size={size} />
          );
        }

        default:
          return getDebugComponent(item, i);
      }
    }

    default:
      return <EmbedWidget {...item} key={i} />;
  }
}

function getBodyItemComponent(item, i, amp, path, articlePublishDate, vertical) {
  switch (item.type) {
    case 'markup':
      return htmlElement(item, i, path);

    case 'embeddedWidget':
      return getWidgetComponent(item, i);

    case 'embeddedImage':
      return getImageComponent(item, i);

    case 'embeddedVideo':
      if (!item.video) {
        return null;
      }
      return (
        <InlineVideo
          addClassNames={classNames({ endmark: item.insertEndmark })}
          video={item.video}
          presentation={item.presentation}
          key={item.video.id}
        />
      );

    case 'hr':
      return <hr key={i} />;

    case 'advertisement':
      return <Ad key={i} offsetViewport={100} slot="boxinline" />;

    case 'taboolaReadMoreBelow':
      return <div id="taboolaReadMoreBelow" key={i} />;

    case 'embeddedVideoPlaylist':
      return null;

    case 'embeddedRecipe':
      return <RecipeCard content={item} key={i} />;

    case 'embeddedSlideshow':
      if (!item.slideshow) {
        return null;
      }
      return <SlideshowCard content={item.slideshow} key={i} />;

    case 'embeddedProduct': {
      if (!item.product) {
        return null;
      }
      const {
        description,
        name,
        offers,
        promotionalMedia,
      } = item.product;
      const {
        externalUrl: productUrl,
        price,
      } = offers[0] || {};
      return (
        <Product
          key={`product-${productUrl}-${price}`}
          addClassNames={classNames({ endmark: item.insertEndmark })}
          name={name}
          imageUrl={get(item, 'product.teaseImage.url.primary', null)}
          productUrl={productUrl}
          offers={offers}
          description={description}
          size={get(item, 'presentation.size', null)}
          promotionalMedia={promotionalMedia}
          amp={amp}
          articlePublishDate={articlePublishDate}
          vertical={vertical}
        />
      );
    }

    case 'embeddedProductList':
      return <ArticleContentSlider content={item} key={i} />;

    case 'embeddedTaxonomy':
      // Hide taxonomy widget. Component rendered in articleBody
      return null;

    case 'externalLink':
      return (
        <p key={i}>
          <Link href={item.url}>Read more here</Link>
          .
        </p>
      );

    case 'inlineNewsletter':
      return <InlineNewsletterSignup {...item.props} key={i} />;

    case 'recommended': {
      return getRecommendationComponent(item, i);
    }

    default:
      return getDebugComponent(item, i);
  }
}

export { getBodyItemComponent };
